import React, { FC, useEffect } from 'react';
import { SUPPORTED_LOCALES } from '@trip-ux/constants';
import labelsEn from './labels-errors-en';
import labelsFr from './labels-errors-fr';
import './error-message.scss';

export enum ErrorType {
  SERVER_ERROR = 'server_error',
  NOT_FOUND = 'not_found',
  FORBIDDEN_ERROR = 'forbidden_error',
  FORBIDDEN_ERROR_UNLICENSED = 'forbidden_error_unlicensed',
  FORBIDDEN_ERROR_WEBLINK = 'forbidden_error_weblink',
  SERVER_ERROR_ADVISOR = 'server_error_advisor'
}

const hideRetryErrors = [ErrorType.FORBIDDEN_ERROR_UNLICENSED, ErrorType.SERVER_ERROR_ADVISOR, ErrorType.FORBIDDEN_ERROR_WEBLINK];

const DEFAULT_ERROR_MESSAGE_KEY = 'errors.message.retry1';

interface ErrorConfig {
  titleKey: string;
  messageKey: string;
  isWeblinkError?: boolean;
}

const errorConfigs: Record<ErrorType, ErrorConfig> = {
  [ErrorType.SERVER_ERROR]: {
    titleKey: 'errors.internalError.title',
    messageKey: DEFAULT_ERROR_MESSAGE_KEY
  },
  [ErrorType.NOT_FOUND]: {
    titleKey: 'errors.notfoundError.title',
    messageKey: DEFAULT_ERROR_MESSAGE_KEY
  },
  [ErrorType.FORBIDDEN_ERROR]: {
    titleKey: 'errors.forbiddenError.title',
    messageKey: DEFAULT_ERROR_MESSAGE_KEY
  },
  [ErrorType.FORBIDDEN_ERROR_UNLICENSED]: {
    titleKey: 'errors.forbiddenErrorUnlicensed.title',
    messageKey: 'errors.forbiddenErrorUnlicensed.message'
  },
  [ErrorType.FORBIDDEN_ERROR_WEBLINK]: {
    titleKey: 'errors.forbiddenErrorWeblink.title',
    messageKey: 'errors.forbiddenErrorWeblink.message',
    isWeblinkError: true
  },
  [ErrorType.SERVER_ERROR_ADVISOR]: {
    titleKey: 'errors.internalErrorAdvisor.title',
    messageKey: 'errors.internalErrorAdvisor.message'
  }
};

// Do not use component / redux - store
export const Error: FC<{ errorType: ErrorType; locale: string }> = ({ errorType, locale }) => {
  // Normalize locale to only supported values, defaulting to English
  const normalizedLocale = locale === SUPPORTED_LOCALES.FRENCH ? SUPPORTED_LOCALES.FRENCH : SUPPORTED_LOCALES.ENGLISH;
  const isFrench = normalizedLocale === SUPPORTED_LOCALES.FRENCH;

  const labels = isFrench ? labelsFr : labelsEn;
  const ariaLabel = isFrench ? 'Aller à la page personnelle' : 'Go to personal page';
  const bannerLogoStyle = isFrench ? 'banner-logo-icon-fr manulife-logo-icon-fr' : 'banner-logo-icon manulife-logo-icon';

  const config = errorConfigs[errorType];
  const title = labels[config.titleKey];
  const errMsg1 = labels[config.messageKey];
  const isWeblinkError = config.isWeblinkError ?? false;

  // Set page title and html lang attribute for accessibility
  useEffect(() => {
    document.title = title;
    document.documentElement.lang = normalizedLocale;
  }, [title, normalizedLocale]);

  return (
    <div className="error-page">
      <div className="error-page-banner banner-image">
        <div className="banner-content">
          <a href="https://www.manulife.ca/personal.html" rel="noopener" aria-label={ariaLabel}>
            <span aria-hidden={true} className={bannerLogoStyle}></span>
          </a>
        </div>
      </div>
      <div className="error-message-card">
        <div className="card-title">
          <h1 data-testid="error-title">{title}</h1>
          <span aria-hidden={true} className="card-logo-icon airplane-logo-icon"></span>
        </div>
        <p>{errMsg1}</p>

        {isWeblinkError && (
          <>
            <ul>
              <li>{labels['errors.forbiddenErrorWeblink.retry1']}</li>
              <li>{labels['errors.forbiddenErrorWeblink.retry2']}</li>
            </ul>
          </>
        )}

        {!hideRetryErrors.includes(errorType) && (
          <ul>
            <li>{labels['errors.message.retry2']}</li>
            <li>{labels['errors.message.retry3']}</li>
            <li>{labels['errors.message.retry4']}</li>
          </ul>
        )}
      </div>
    </div>
  );
};

Error.displayName = 'Error';
export default Error;
